/**
 * Tiny SWR-style cache for slow backend calls.
 *
 * Pattern:
 *   const data = await cached("vorgang.all", 30_000, () => vorgangService.getAllVorgaenge());
 *
 * What you get:
 *   - **Memory cache** keyed by string, with a per-entry TTL.
 *   - **localStorage backing** so a hard reload still renders instantly with
 *     stale data while a background refetch runs.
 *   - **Stale-while-revalidate**: when the cached value is past its TTL but
 *     still on disk, `cached()` returns it immediately and kicks off the
 *     fetch in the background. Subscribers receive an update via the
 *     `onUpdate` callback once the fresh data lands.
 *   - **In-flight de-duplication**: simultaneous calls share one promise.
 *
 * Designed to wrap REST fetches that don't tolerate the round-trip latency of
 * the local dev backend without burdening callers with a hook lifecycle.
 *
 * Not a replacement for `swr` / `react-query` — just enough for our hot lists
 * (vorgaenge, mail inbox, contacts).
 */

type Entry<T> = {
  value: T;
  storedAt: number; // epoch ms
};

const MEMORY = new Map<string, Entry<unknown>>();
const INFLIGHT = new Map<string, Promise<unknown>>();
const LS_PREFIX = "swrcache:";

function readDisk<T>(key: string): Entry<T> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw) as Entry<T>;
  } catch {
    return null;
  }
}

function writeDisk<T>(key: string, entry: Entry<T>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry));
  } catch {
    // ignore quota / SecurityError — memory cache still works
  }
}

function evictDisk(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LS_PREFIX + key);
  } catch {
    /* noop */
  }
}

export type CachedOpts<T> = {
  /** Time-to-live in ms before the entry is considered stale. */
  ttlMs: number;
  /**
   * Called once the background refetch lands with new data (only fires when
   * the cached value was stale and a revalidation happened). Use it to
   * `setState` in React.
   */
  onUpdate?: (fresh: T) => void;
  /**
   * If true, ignore the cache and always refetch. Still populates the cache.
   */
  force?: boolean;
  /**
   * Wenn `false`: NUR die Memory-Map nutzen, NIE nach localStorage schreiben
   * oder von dort lesen (Default `true`). Pflicht für große oder sensible Werte
   * — allen voran Base64-Inline-Bodies von Mails: die sprengen die
   * localStorage-Quota; der Silent-Catch in `writeDisk` würde dann zwar nicht
   * crashen, aber der Browser verdrängt beim Quota-Überlauf ANDERE Einträge, was
   * fremde Caches beschädigt. Nebeneffekt: memory-only behält die echten Objekte
   * (z. B. `Date`), umgeht also das JSON-Rehydrierungsproblem des Disk-Caches.
   */
  persist?: boolean;
};

/**
 * Stale-while-revalidate fetch wrapper.
 *
 * @returns the cached value if available (even if stale), otherwise the
 *   freshly-fetched value. When stale, fires `opts.onUpdate(fresh)` after the
 *   background refetch finishes.
 */
export async function cached<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts: CachedOpts<T>,
): Promise<T> {
  const now = Date.now();
  const persist = opts.persist !== false;
  const fromMem = MEMORY.get(key) as Entry<T> | undefined;
  // Bei persist=false NIE von der Disk lesen — nur die Memory-Map ist gültig.
  const fromDisk = fromMem ?? (persist ? readDisk<T>(key) : null);

  // Force refresh — bypass cache entirely.
  if (opts.force) {
    return doFetch(key, fetcher, persist);
  }

  if (fromDisk) {
    const isFresh = now - fromDisk.storedAt < opts.ttlMs;
    if (isFresh) {
      // Promote disk entry to memory if it wasn't already there.
      if (!fromMem) MEMORY.set(key, fromDisk);
      return fromDisk.value;
    }
    // Stale — return it immediately, kick off a background refresh.
    void doFetch(key, fetcher, persist).then((fresh) => {
      try {
        opts.onUpdate?.(fresh);
      } catch (err) {
        console.error("[swrCache] onUpdate handler threw", err);
      }
    }).catch((err) => {
      console.error(`[swrCache] background refresh failed for "${key}"`, err);
    });
    return fromDisk.value;
  }

  // Nothing cached at all — must block on the fetch.
  return doFetch(key, fetcher, persist);
}

async function doFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  persist: boolean,
): Promise<T> {
  const inflight = INFLIGHT.get(key) as Promise<T> | undefined;
  if (inflight) return inflight;

  const p = (async () => {
    try {
      const value = await fetcher();
      const entry: Entry<T> = { value, storedAt: Date.now() };
      MEMORY.set(key, entry);
      if (persist) writeDisk(key, entry);
      return value;
    } finally {
      INFLIGHT.delete(key);
    }
  })();
  INFLIGHT.set(key, p);
  return p;
}

/** Explicitly invalidate a cache entry (e.g. after a mutation). */
export function invalidate(key: string): void {
  MEMORY.delete(key);
  evictDisk(key);
}

/** Invalidate every cache key that starts with the given prefix. */
export function invalidateByPrefix(prefix: string): void {
  for (const k of Array.from(MEMORY.keys())) {
    if (k.startsWith(prefix)) MEMORY.delete(k);
  }
  if (typeof window === "undefined") return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX + prefix)) {
        window.localStorage.removeItem(k);
      }
    }
  } catch {
    /* noop */
  }
}

/**
 * Overwrite a cache entry in place with a client-side-enriched value (kein
 * Refetch). Respektiert `persist` genau wie `cached`: bei `persist:false`
 * NUR die Memory-Map (und eine evtl. vorhandene Disk-Kopie wird entfernt) —
 * Pflicht für große/sensible Werte wie die Base64-Inline-Bodies von Mails
 * (Mail-SP7 patcht `cid:`→`data:` und legt die fertige Fassung zurück, damit
 * erneutes Öffnen ohne Neu-Fetch rendert).
 */
export function poke<T>(key: string, value: T, persist = true): void {
  const entry: Entry<T> = { value, storedAt: Date.now() };
  MEMORY.set(key, entry);
  if (persist) writeDisk(key, entry);
  else evictDisk(key);
}

/** Convenience: peek a cache entry without triggering a refetch. */
export function peek<T>(key: string): T | undefined {
  const fromMem = MEMORY.get(key) as Entry<T> | undefined;
  if (fromMem) return fromMem.value;
  const fromDisk = readDisk<T>(key);
  return fromDisk?.value;
}
